import { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { getBackendUrl } from '../config';

const BACKEND_URL = getBackendUrl();

const Login = () => {
  const { language, setLanguage, t } = useLanguage();
  const [theme, setTheme] = useState('light');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [remember, setRemember] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { login } = useAuth();

  useEffect(() => {
    // Load theme from localStorage
    const savedTheme = localStorage.getItem('dio-theme');
    const prefersDark = window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;
    const initialTheme = savedTheme ? savedTheme : (prefersDark ? 'dark' : 'light');
    setTheme(initialTheme);
    document.documentElement.classList.toggle('dark', initialTheme === 'dark');
  }, []);

  const toggleTheme = () => {
    const newTheme = theme === 'dark' ? 'light' : 'dark';
    setTheme(newTheme);
    localStorage.setItem('dio-theme', newTheme);
    document.documentElement.classList.toggle('dark', newTheme === 'dark');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await axios.post(`${BACKEND_URL}/api/auth/login`, {
        username,
        password,
        remember
      });

      // Use the login function from AuthContext
      login(response.data.access_token, response.data.user);

      // Redirect to dashboard
      navigate('/dashboard', { replace: true });
    } catch (err) {
      console.error('Login error:', err);
      const isNetwork = !err.response || (err.message && String(err.message).toLowerCase().includes('network'));
      setError(isNetwork ? t('errorNetwork') : (err.response?.data?.detail || t('errorLogin')));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-page">
      <div
        className="bg"
        aria-hidden="true"
        style={{
          backgroundImage: theme === 'dark'
            ? `url('/assets/images/bg-dark.png')`
            : `url('/assets/images/bg-light.png')`
        }}
      ></div>

      <div className="page">
        <section className="card" role="region" aria-label="Login">
          <div className="head">
            <div className="logo-wrap">
              {/* logo-dio-iot.png = blu/ngjyrë (light), logo-dio-iot-white.png = e bardhë (dark) */}
              <img
                className="logo-light"
                src={`${process.env.PUBLIC_URL || ''}/assets/images/logo-dio-iot.png`}
                alt="DIO IOT"
                onError={(e) => { e.target.onerror = null; e.target.src = `${process.env.PUBLIC_URL || ''}/assets/images/logo-full.svg`; }}
              />
              <img
                className="logo-dark"
                src={`${process.env.PUBLIC_URL || ''}/assets/images/logo-dio-iot-white.png`}
                alt="DIO IOT"
                onError={(e) => { e.target.onerror = null; e.target.src = `${process.env.PUBLIC_URL || ''}/assets/images/logo-white-full.svg`; }}
              />
            </div>

            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={() => setLanguage('sq')}
                title="Shqip (Kosovo)"
                className={`rounded border-2 transition overflow-hidden ${language === 'sq' ? 'border-blue-500 ring-2 ring-blue-500/30' : 'border-transparent hover:border-gray-400'}`}
              >
                <img src="https://flagcdn.com/w40/xk.png" alt="Kosovo" className="w-7 h-5 object-cover" />
              </button>
              <button
                type="button"
                onClick={() => setLanguage('en')}
                title="English (United Kingdom)"
                className={`rounded border-2 transition overflow-hidden ${language === 'en' ? 'border-blue-500 ring-2 ring-blue-500/30' : 'border-transparent hover:border-gray-400'}`}
              >
                <img src="https://flagcdn.com/w40/gb.png" alt="United Kingdom" className="w-7 h-5 object-cover" />
              </button>
              <button
                type="button"
                className="theme-btn"
                onClick={toggleTheme}
                aria-label={t('toggleThemeLabel')}
              >
              <span style={{ fontSize: '18px', lineHeight: 1 }}>🌙</span>
              </button>
            </div>
          </div>

          <div className="title">{t('loginTitle')}</div>
          <div className="subtitle">{t('loginSubtitle')}</div>

          {error && (
            <div className="errors">
              <ul>
                <li>{error}</li>
              </ul>
            </div>
          )}

          <form onSubmit={handleSubmit} autoComplete="on" id="dioLoginForm">
            <div className="field">
              <label htmlFor="login">{t('username')}</label>
              <div className="input-wrap">
                <svg className="icon" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <path
                    d="M20 21a8 8 0 0 0-16 0"
                    stroke="currentColor"
                    strokeOpacity=".75"
                    strokeWidth="2"
                    strokeLinecap="round"
                  />
                  <path
                    d="M12 13a5 5 0 1 0 0-10 5 5 0 0 0 0 10Z"
                    stroke="currentColor"
                    strokeOpacity=".75"
                    strokeWidth="2"
                  />
                </svg>

                <input
                  id="login"
                  type="text"
                  name="login"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  required
                  autoFocus
                  autoComplete="username"
                  placeholder={t('username')}
                />
              </div>
            </div>

            <div className="field">
              <label htmlFor="password">{t('passwordLabel')}</label>
              <div className="input-wrap">
                <svg className="icon" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <path
                    d="M7 11V8a5 5 0 0 1 10 0v3"
                    stroke="currentColor"
                    strokeOpacity=".75"
                    strokeWidth="2"
                    strokeLinecap="round"
                  />
                  <path
                    d="M6 11h12a2 2 0 0 1 2 2v7a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2Z"
                    stroke="currentColor"
                    strokeOpacity=".75"
                    strokeWidth="2"
                  />
                </svg>

                <input
                  id="password"
                  type="password"
                  name="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  autoComplete="current-password"
                  placeholder={t('passwordLabel')}
                />
              </div>
            </div>

            <div className="row">
              <input
                id="remember"
                type="checkbox"
                name="remember"
                checked={remember}
                onChange={(e) => setRemember(e.target.checked)}
              />
              <span>{t('rememberCreds')}</span>
            </div>

            <button className="btn" type="submit" disabled={loading}>
              {loading ? t('loggingIn') : t('loginBtn')}
            </button>
          </form>
        </section>
      </div>
    </div>
  );
};

export default Login;
